import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { useUpdateMerchandisingCollection } from '@/hooks/useUpdateMerchandisingCollection';
import i18n from '@/lib/i18n';
import { MerchandisingCollection } from '@/services/MerchandisingCollectionsService';

const formSchema = z.object({
  name: z.string().min(1, {
    message: i18n.t(
      'pages.MerchandisingCollections.pages.Collection.components.CollectionInfo.form.nameError',
    ),
  }),
  description: z.string().min(1, {
    message: i18n.t(
      'pages.MerchandisingCollections.pages.Collection.components.CollectionInfo.form.descriptionError',
    ),
  }),
});

type FormData = z.infer<typeof formSchema>;

interface CollectionFormProps {
  collection: MerchandisingCollection;
}

export function CollectionForm({ collection }: CollectionFormProps) {
  const [editable, setEditable] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Collection.components.CollectionInfo',
  });

  const { updateMerchandisingCollection, isLoading } =
    useUpdateMerchandisingCollection();

  const {
    formState: { errors, isSubmitting },
    register,
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    disabled: !editable || isLoading,
    defaultValues: {
      name: collection.name,
      description: collection.description,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateMerchandisingCollection({
        id: collection.id,
        dto: { name: data.name, description: data.description },
      });

      toast.success(t('toast.success'));
      setEditable(false);
    } catch {
      toast.error(t('toast.error'));
    }
  });

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <FormGroup error={{ message: errors.name?.message, font: 'sm' }}>
        <Label htmlFor="collection-name">{t('form.nameLabel')}</Label>
        <Input id="collection-name" {...register('name')} />
      </FormGroup>

      <FormGroup error={{ message: errors.description?.message, font: 'sm' }}>
        <Label htmlFor="collection-description">
          {t('form.descriptionLabel')}
        </Label>
        <Textarea
          id="collection-description"
          rows={3}
          {...register('description')}
        />
      </FormGroup>

      <div className="flex items-center gap-2">
        {!editable && (
          <Button type="button" onClick={() => setEditable(true)}>
            {t('form.updateButtonLabel')}
          </Button>
        )}

        {editable && (
          <>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t('form.submitButtonLabelLoading')
                : t('form.submitButtonLabel')}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => setEditable(false)}
            >
              {t('form.cancelButtonLabel')}
            </Button>
          </>
        )}
      </div>
    </form>
  );
}
